<template>
  <UseItemConfirmationModal
    v-bind:is-open="useItemConfirmationModalIsOpen"
    :pokemon="pokemon"
    :item="currentSelectedItem"
    @update:is-open="useItemConfirmationModalIsOpen = $event"
    @useItem="
      async () => {
        const result = await useItem(pokemon.id, currentSelectedItem);

        if (result) {
          useItemConfirmationModalIsOpen = false;
          useItemSuccessModalIsOpen = true;
        }
      }
    "
  />

  <UseItemSuccessModal
    :isOpen="useItemSuccessModalIsOpen"
    @update:is-open="
      () => {
        useItemSuccessModalIsOpen = $event;
        if (!$event) {
          $emit('usedItem');
        }
      }
    "
  />

  <Modal :is-open="isOpen" :closeable="false">
    <div class="row p-2">
      <div
        class="col-3 item-list__entry"
        v-for="item in usableItems"
        :key="item.id"
      >
        <ItemEntryButton
          :id="item.id"
          :name="item.name"
          :sprite_name="item.sprite_name"
          :amount="item.amount"
          :category="item.category"
          :tmType='item.tmType'
          @click="
            async () => {
              $emit('update:is-item-modal-open', false);

              if (needConfirmation(item)) {
                openConfirmation(item);
              } else {
                const result = await useItem(pokemon.id, item);

                if (result) {
                  $emit('usedItem');
                }
              }
            }
          "
        />
      </div>
    </div>

    <template #footer>
      <button
        v-if="hasInfoButton"
        type="button"
        class="btn btn-secondary"
        style="background-color: #dc1818"
        @click="
          $emit('update:is-item-modal-open', false);
          $emit('update:is-pokemon-info-open', true);
        "
      >
        {{ translation.battle.info }}
      </button>
      <button
        v-if="hasRemoveFromTeamButton"
        type="button"
        class="btn btn-secondary"
        @click="
          $emit('update:is-item-modal-open', false);
          removeFromTeam(pokemon);
        "
      >
        {{ translation.battle.removeFromTeam }}
      </button>
      <button
        type="button"
        class="btn btn-secondary"
        @click="$emit('update:is-item-modal-open', false)"
      >
        {{ translation.general.cancel }}
      </button>
    </template>
  </Modal>
</template>

<style scoped lang="scss">
.item-list {
  &__entry {
    padding: 0;
    margin-bottom: 20px;
  }
}
</style>

<script>
import UseItemSuccessModal from "./UseSuccessModal/UseItemSuccessModal.vue";
import ItemEntryButton from "../ItemEntryButton/ItemEntryButton.vue";
import Modal from "../Modal/Modal.vue";
import { translation } from "@/utils/translations";
import { ref } from "@vue/reactivity";
import {
  startLoadingProcess,
  stopLoadingProcess,
} from "../LoadingOverlay/loadingOverlayControl";
import { PCGApi } from "../../services/pcg";
import { handleUnknownError } from "@/utils/utilities";
import UseItemConfirmationModal from "./UseItemConfirmationModal/UseItemConfirmationModal.vue";
import { showModalMsg } from "@/components/Modal/modalUtils";
import { useQueryClient } from "vue-query";
import Cache from "../../cache";

export default {
  components: {
    Modal,
    ItemEntryButton,
    UseItemConfirmationModal,
    UseItemSuccessModal,
  },
  props: {
    isOpen: {
      type: Boolean,
      value: false,
    },
    pokemon: {
      type: Object,
      value: {},
    },
    hasInfoButton: {
      type: Boolean,
      value: false,
    },
    hasRemoveFromTeamButton: {
      type: Boolean,
      value: false,
    },
  },
  emits: [
    "update:is-item-modal-open",
    "update:is-pokemon-info-open",
    "removedFromTeam",
    "usedItem",
  ],
  watch: {
    isOpen: async function (newVal) {
      if (newVal) {
        await this.getUsableItems();
      }
    },
  },
  setup(props, { emit }) {
    const queryClient = useQueryClient();

    const usableItems = ref([]);

    const getUsableItems = async () => {
      startLoadingProcess();
      try {
        const response = await Cache.Inventory.getPokemonUsableItems(
          queryClient
        );

        usableItems.value = response.allItems;
      } catch (error) {
        handleUnknownError(error);
      }

      stopLoadingProcess();
    };

    const useItemConfirmationModalIsOpen = ref(false);
    const currentSelectedItem = ref({});
    const openConfirmation = (item) => {
      currentSelectedItem.value = item;
      useItemConfirmationModalIsOpen.value = true;
    };
    const useItemSuccessModalIsOpen = ref(false);

    const needConfirmation = (item) => {
      if (item.sell_price !== null) {
        // some items don't have prices
        if (item.sell_price > 999) {
          // price needs to be over 1000 to need confirmation
          currentSelectedItem.value = item;
          useItemConfirmationModalIsOpen.value = true;
          return true;
        }
      }

      return false;
    };

    const useItem = async (pokemon_id, item) => {
      startLoadingProcess();
      try {
        await PCGApi.Trainer.useItem(pokemon_id, item.id);
        stopLoadingProcess();

        Cache.Inventory.invalidatePokeUsable(queryClient);
        
        await getUsableItems();
        return true;
      } catch (error) {
        if (error.response) {
          if (error.response.data.error === 1) {
            showModalMsg(translation.itemList.itemNotExists);
          } else if (error.response.data.error === 2) {
            showModalMsg(translation.itemList.itemAlreadyActive);
          } else if (error.response.data.error === 10) {
            showModalMsg(translation.itemList.itemNoBuddy);
          } else if (error.response.data.error === 100) {
            showModalMsg(translation.itemList.PokemonFullHp);
          } else if (error.response.data.error === 101) {
            showModalMsg(translation.itemList.PokemonDead);
          } else if (error.response.data.error === 102) {
            showModalMsg(translation.itemList.PokemonStillAlive);
          } else if (error.response.data.error === 103) {
            showModalMsg(translation.itemList.PokemonNoStatusCondition);
          } else if (error.response.data.error === 104) {
            showModalMsg(translation.itemList.PokemonFullPP);
          } else if (error.response.data.error === 105) {
            showModalMsg('This item can only be used one time per Pokemon. This Pokemon already used it.');
          } else if (error.response.data.error === 106) {
            showModalMsg('This item cannot be used on this Pokemon.');
          } else if (error.response.data.error === 107) {
            showModalMsg('This pokemon already knows that move!');
          }else {
            handleUnknownError(error);
          }

          stopLoadingProcess();
          return false;
        }
      }
    };

    const removeFromTeam = async (pkm) => {
      startLoadingProcess();
      try {
        await PCGApi.Trainer.removeFromTeam(pkm.id);
        emit("removedFromTeam");
      } catch (error) {
        if (error.response.data.error === 1) {
          showModalMsg(translation.pokemonList.PokemonNotInTeam);
        } else {
          handleUnknownError(error);
        }
      }

      stopLoadingProcess();
    };

    return {
      translation,
      usableItems,
      getUsableItems,

      useItemSuccessModalIsOpen,
      useItemConfirmationModalIsOpen,
      currentSelectedItem,
      needConfirmation,
      openConfirmation,
      useItem,

      removeFromTeam,
    };
  },
};
</script>
